import { createFileRoute } from '@tanstack/react-router';
import { isApiError } from '@pc/api/client';
import { loginUrl, useMe } from '../auth/useMe';
import { LEVEL, meetsLevel } from '../auth/levels';
import { ApiErrorNotice } from '../components/ApiErrorNotice';
import { Card, Loading } from '../components/ui';
import { ChainVerification } from '../components/audit/ChainVerification';
import { OpIdLookup } from '../components/audit/OpIdLookup';
import { isAuditTargetType } from '../components/audit/RecordsTable';
import { SearchFilters, type AuditFilterQuery } from '../components/audit/SearchFilters';

/**
 * The filters plus a standalone `opId` lookup, all round-tripped through the
 * URL so a search built while chasing an incident can be pasted into a
 * ticket or handed to someone else and reproduce exactly.
 */
type AuditSearch = AuditFilterQuery & { opId?: string };

function str(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function num(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

function outcomeOf(value: unknown): AuditFilterQuery['outcome'] {
  return value === 'SUCCESS' || value === 'FAILURE' || value === 'DENIED' ? value : undefined;
}

function viaOf(value: unknown): AuditFilterQuery['via'] {
  return value === 'discord' || value === 'api' || value === 'break-glass' ? value : undefined;
}

/**
 * The third enumerated filter, dropped on a non-member exactly as the two above
 * are: a URL is hand-edited, pasted from a ticket and outlived by the vocabulary
 * it was written against, and passing an unrecognised value straight through
 * would turn the whole search into a 400 instead of the trail the operator came
 * for. Its members are read from the contract rather than listed here, since
 * there are twelve of them and the backend adds more.
 */
function targetTypeOf(value: unknown): AuditFilterQuery['targetType'] {
  return typeof value === 'string' && isAuditTargetType(value) ? value : undefined;
}

/**
 * Swaps just the op id, keeping the search filters untouched. Built by
 * dropping the old key and conditionally re-adding it — `exactOptionalPropertyTypes`
 * treats `{ opId: undefined }` as distinct from (and here, invalid next to) simply
 * omitting `opId`, so clearing it has to be a deletion, not an assignment.
 */
function withOpId(prev: AuditSearch, opId: string | undefined): AuditSearch {
  const { opId: _current, ...filters } = prev;
  return { ...filters, ...(opId !== undefined && { opId }) };
}

/** Swaps the filters, keeping whatever op id lookup is in progress. */
function withFilters(prev: AuditSearch, next: AuditFilterQuery): AuditSearch {
  const { opId } = prev;
  return { ...(opId !== undefined && { opId }), ...next };
}

/**
 * The URL, reduced to the filters this screen recognises.
 *
 * No search-param library is in play here (see package.json) — this is a plain
 * parser, tolerant of a hand-edited or stale URL rather than throwing on one.
 *
 * It is a named function rather than an inline `validateSearch` because the
 * screen has to run it a second time on what the router hands back. The router
 * does not replace the parsed search with a validator's output, it merges over
 * it — `preMatchSearch = { ...parentSearch, ...strictSearch }` in
 * router-core's matchRoutes — so every key this parser deliberately omits is
 * still sitting in `useSearch()`'s result, and anything spreading that into a
 * request sends the junk straight on to the API. That is not hypothetical: it
 * is how `?outcome=BOGUS` used to reach `/api/audit` and turn a whole search
 * into a 400.
 */
function parseAuditSearch(raw: Record<string, unknown>): AuditSearch {
  const opId = str(raw.opId);
  const actorDiscordID = str(raw.actorDiscordID);
  const actorOktaId = str(raw.actorOktaId);
  const subjectDiscordID = str(raw.subjectDiscordID);
  const targetType = targetTypeOf(raw.targetType);
  const targetId = str(raw.targetId);
  const outcome = outcomeOf(raw.outcome);
  const via = viaOf(raw.via);
  const command = str(raw.command);
  const subcommand = str(raw.subcommand);
  const since = str(raw.since);
  const until = str(raw.until);
  const limit = num(raw.limit);

  return {
    ...(opId !== undefined && { opId }),
    ...(actorDiscordID !== undefined && { actorDiscordID }),
    ...(actorOktaId !== undefined && { actorOktaId }),
    ...(subjectDiscordID !== undefined && { subjectDiscordID }),
    ...(targetType !== undefined && { targetType }),
    ...(targetId !== undefined && { targetId }),
    ...(outcome !== undefined && { outcome }),
    ...(via !== undefined && { via }),
    ...(command !== undefined && { command }),
    ...(subcommand !== undefined && { subcommand }),
    ...(since !== undefined && { since }),
    ...(until !== undefined && { until }),
    ...(limit !== undefined && { limit }),
  };
}

export const Route = createFileRoute('/audit')({
  validateSearch: parseAuditSearch,
  component: AuditPage,
});

/**
 * The audit trail is what gets pulled up mid-incident, so every branch below
 * matters: a stale session, a real refusal and "nobody could check" must
 * never be told to the operator as the same thing.
 */
function AuditPage() {
  const me = useMe();

  if (me.isPending) return <Loading what="your access level" />;

  if (me.isError) {
    // 401 is "not logged in", not a failure to report.
    if (isApiError(me.error) && me.error.kind === 'unauthenticated') {
      return (
        <Card title="Audit">
          <p>You need to log in to view the audit trail.</p>
          <a href={loginUrl()}>Log in with Okta</a>
        </Card>
      );
    }
    return <ApiErrorNotice error={me.error} onRetry={() => void me.refetch()} />;
  }

  // Display-only: the API enforces this regardless. The point is to explain
  // why nothing loads rather than let the operator watch three panels 403.
  if (!meetsLevel(me.data.level, LEVEL.PRIVILEGED)) {
    return (
      <Card title="Audit">
        <p role="alert">
          Audit access requires level {LEVEL.PRIVILEGED}. Your account is level {me.data.level} (
          {me.data.levelSource}), so this screen would only get a refusal back from the API. Ask
          for an escalation if you need to review the trail for an incident.
        </p>
      </Card>
    );
  }

  return <AuditScreen />;
}

function AuditScreen() {
  const navigate = Route.useNavigate();
  // Parsed again here, not merely read: what `useSearch` returns still carries
  // every URL parameter this route declined to validate (see
  // `parseAuditSearch`), and these filters are handed almost verbatim to the
  // API. The types say otherwise — the router's generated types describe this
  // as the validated shape — which is exactly why the leak was invisible.
  const { opId, ...filters } = parseAuditSearch(Route.useSearch());

  return (
    <>
      <h1>Audit trail</h1>

      <Card title="Search">
        {/* Same remount-on-external-change reasoning, keyed on the filter
            content rather than identity. */}
        <SearchFilters
          key={JSON.stringify(filters)}
          filters={filters}
          onApply={(next) => void navigate({ search: (prev) => withFilters(prev, next) })}
        />
      </Card>

      <Card title="Look up an operation">
        {/* Remounts when the committed op id changes externally (back
            button, a pasted link) so its draft input state does not go
            stale against the URL. */}
        <OpIdLookup
          key={opId ?? ''}
          opId={opId}
          onOpIdChange={(nextOpId) => void navigate({ search: (prev) => withOpId(prev, nextOpId) })}
        />
      </Card>

      <Card title="Chain verification">
        <ChainVerification />
      </Card>
    </>
  );
}
